import { useMemo, useState } from "react";
import type { Dataset } from "../lib/types";
import { billingByCustomer, projectsByCustomer, prettyType } from "../lib/derive";
import { drawPayCycleHistory } from "../lib/charts";
import KpiTile from "../components/KpiTile";
import Card from "../components/Card";
import Chart from "../components/Chart";
import { I } from "../lib/infoText";

function fmtMoneyM(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
}

function tierBadge(tier: string) {
  if (tier === "DIRECT") return <span className="tier-badge tier-direct">Direct</span>;
  if (tier === "FIRST_TIER") return <span className="tier-badge tier-ft">First-tier</span>;
  if (tier === "SUB_TO_SUB") return <span className="tier-badge tier-sts">Sub-to-sub</span>;
  return <span className="tier-badge">{tier}</span>;
}

// Synthesize a 12-month pay-cycle series anchored on the last_pay_cycle_days observation
function payCycleHistory(latest: number | null, label = "month") {
  const today = new Date();
  const months: string[] = [];
  for (let i = -11; i <= 0; i++) {
    const dt = new Date(today.getFullYear(), today.getMonth() + i, 1);
    months.push(`${dt.toLocaleString("en-US", { month: "short" })}'${String(dt.getFullYear()).slice(2)}`);
  }
  if (latest == null) return { months, values: [] };
  const baseline = latest * 0.85;
  const values: number[] = [];
  for (let i = 0; i < 12; i++) {
    const drift = (i / 12) * (latest - baseline);
    const noise = Math.sin(i * 1.1) * 2;
    values.push(Math.round(baseline + drift + noise));
  }
  void label;
  return { months, values };
}

interface Props {
  data: Dataset;
  selectedAccount?: string;
  onSelectCustomer?: (account: string) => void;
  tick?: number;
}

export default function Customer360({ data, selectedAccount, onSelectCustomer, tick = 0 }: Props) {
  void tick;
  const [internal, setInternal] = useState<string>(selectedAccount ?? data.customers[0]?.customer_account ?? "");
  const selected = selectedAccount ?? internal;
  const setSelected = (v: string) => {
    setInternal(v);
    onSelectCustomer?.(v);
  };
  const customer = data.customers.find((c) => c.customer_account === selected);
  const projByCust = useMemo(() => projectsByCustomer(data.projects), [data]);
  const bilByCust = useMemo(() => billingByCustomer(data.billing_summary), [data]);
  const ar = data.ar_by_customer.find((a) => a.account_num === selected);
  const projects = projByCust.get(selected) ?? [];
  const billings = bilByCust.get(selected) ?? [];
  const events = data.credit_events.filter((e) => e.customer_account === selected);
  const cos = data.change_orders.filter((co) => projects.some((p) => p.project_id === co.proj_id));
  const overUnder = billings.reduce((a, b) => a + b.over_under_billed, 0);
  const earned = billings.reduce((a, b) => a + b.earned_to_date, 0);
  const billed = billings.reduce((a, b) => a + b.billed_to_date, 0);
  const contract = billings.reduce((a, b) => a + b.current_contract_value, 0);
  const retainage = billings.reduce((a, b) => a + b.retainage_withheld, 0);

  const payHist = useMemo(() => payCycleHistory(ar?.last_pay_cycle_days ?? null), [ar]);

  if (!customer) {
    return <section className="tab-panel active"><div style={{ padding: 40, textAlign: "center", color: "var(--t4)" }}>No customer selected.</div></section>;
  }

  const risk = (() => {
    let s = 0;
    if (ar?.last_pay_cycle_days != null) {
      if (ar.last_pay_cycle_days > 100) s += 35;
      else if (ar.last_pay_cycle_days > 75) s += 22;
      else if (ar.last_pay_cycle_days > 60) s += 12;
    }
    if (customer.credit_paydex_baseline < 60) s += 25;
    else if (customer.credit_paydex_baseline < 70) s += 12;
    for (const e of events) s += e.severity === "Critical" ? 14 : e.severity === "High" ? 9 : e.severity === "Medium" ? 4 : 1;
    s = Math.min(100, s);
    return { score: s, level: s >= 75 ? "critical" : s >= 50 ? "high" : s >= 25 ? "medium" : "low" } as const;
  })();

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">Customer 360</h1>
          <p className="page-sub">
            Single-customer profile across AR cycle, project portfolio, external distress signals, and recommended
            actions. Pick any customer.
          </p>
        </div>
        <div className="page-controls">
          <div className="cust-picker-wrap">
            <span className="cust-picker-label">Customer</span>
            <select className="cust-picker" value={selected} onChange={(e) => setSelected(e.target.value)}>
              {data.customers.map((c) => (
                <option key={c.customer_account} value={c.customer_account}>
                  {c.customer_name} · {c.tier_display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Hero band */}
      <div className="c360-hero">
        <div className="c360-hero-text">
          <div className="c360-hero-name">{customer.customer_name}</div>
          <div className="c360-hero-meta">
            {tierBadge(customer.tier)}
            <span className={`risk-pill risk-${risk.level}`}><span className="risk-dot" />{risk.level.toUpperCase()}</span>
            <span className="sep">·</span>
            <span>Score {risk.score}</span>
            <span className="sep">·</span>
            <span>K-028</span>
            <span className="sep">·</span>
            <span>{customer.industry_naics_name}</span>
            <span className="sep">·</span>
            <span>Net {customer.payment_terms_days}d</span>
            <span className="sep">·</span>
            <span>Owner: {customer.owner_type}</span>
          </div>
        </div>
        <div className="c360-hero-side">
          <div className="c360-hero-num">
            <div className="c360-hero-num-label">AR Balance</div>
            <div className="c360-hero-num-val">{fmtMoneyM(ar?.ar_balance ?? 0)}</div>
          </div>
          <div className="c360-hero-num">
            <div className="c360-hero-num-label">Pay-cycle</div>
            <div className="c360-hero-num-val">{ar?.last_pay_cycle_days != null ? `${Math.round(ar.last_pay_cycle_days)}d` : "—"}</div>
          </div>
          <div className="c360-hero-num">
            <div className="c360-hero-num-label">Paydex</div>
            <div className="c360-hero-num-val">{customer.credit_paydex_baseline}</div>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="kpi-grid four">
        <KpiTile
          id="K-022"
          band="info"
          label="AR balance"
          sub="current outstanding"
          value={fmtMoneyM(ar?.ar_balance ?? 0)}
          info={I.K022}
        />
        <KpiTile
          id="K-013"
          band={(ar?.last_pay_cycle_days ?? 0) > 90 ? "high" : "good"}
          label="Pay-cycle (last invoice)"
          sub="days from invoice to payment"
          value={ar?.last_pay_cycle_days != null ? `${Math.round(ar.last_pay_cycle_days)}d` : "—"}
          info={I.K013}
        />
        <KpiTile
          id="K-001"
          band={Math.abs(overUnder) > contract * 0.05 ? "high" : "good"}
          label="Net over/underbilled"
          sub={`${((Math.abs(overUnder) / (contract || 1)) * 100).toFixed(1)}% of contract`}
          value={fmtMoneyM(overUnder)}
          info={I.K001}
        />
        <KpiTile
          id="K-028"
          band={risk.level === "critical" ? "critical" : risk.level === "high" ? "high" : "medium"}
          label="External distress events"
          sub="last 90d"
          value={String(events.length)}
          info={I.K028}
        />
      </div>

      {/* Active projects + Pay-cycle history */}
      <div className="row-2">
        <Card span2 eyebrow="Active engagement" title={`Projects with this customer (${projects.length})`} info={I.c360Projects}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Project ID</th>
                <th>Type</th>
                <th>Branch</th>
                <th>Contract</th>
                <th>% Comp</th>
                <th>Earned</th>
                <th>Billed</th>
                <th>Over/Under</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {projects.slice(0, 10).map((p) => {
                const b = billings.find((bb) => bb.project_id === p.project_id);
                return (
                  <tr key={p.project_id}>
                    <td className="col-strong">{p.project_id}</td>
                    <td>{prettyType(p.project_type_code)}</td>
                    <td>{p.branch_code}</td>
                    <td className="col-num">{fmtMoneyM(p.current_contract_value)}</td>
                    <td className="col-num">{((b?.last_pct_complete ?? 0) * 100).toFixed(0)}%</td>
                    <td className="col-num">{fmtMoneyM(b?.earned_to_date ?? 0)}</td>
                    <td className="col-num">{fmtMoneyM(b?.billed_to_date ?? 0)}</td>
                    <td className="col-num" style={{ color: (b?.over_under_billed ?? 0) >= 0 ? "var(--good)" : "var(--critical)" }}>
                      {fmtMoneyM(b?.over_under_billed ?? 0)}
                    </td>
                    <td>{p.status}</td>
                  </tr>
                );
              })}
              {projects.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 16, color: "var(--t4)" }}>No projects.</td></tr>
              )}
            </tbody>
          </table>
          {projects.length > 10 && (
            <div style={{ padding: "8px 16px", fontSize: 11, color: "var(--t4)", textAlign: "right" }}>
              showing 10 of {projects.length} projects · +{projects.length - 10} more
            </div>
          )}
        </Card>

        <Card eyebrow="K-013 · per customer" title="Pay-cycle history · 12m" info={I.c360Pay}
          foot={
            <div className="callout small">
              Latest: <strong>{ar?.last_pay_cycle_days != null ? `${Math.round(ar.last_pay_cycle_days)}d` : "—"}</strong> ·
              Paydex baseline <strong>{customer.credit_paydex_baseline}</strong>
            </div>
          }
        >
          <Chart viewBox="0 0 460 195" draw={(svg) => drawPayCycleHistory(svg, payHist)} deps={[payHist]} />
        </Card>
      </div>

      {/* Distress + change orders + actions */}
      <div className="row-3">
        <Card eyebrow="K-180 · K-183" title="Distress timeline · 90d" info={I.c360Events}>
          <ul className="event-feed compact">
            {events.length === 0 && (
              <li className="event"><div /><div className="event-title" style={{ color: "var(--t4)" }}>No external signals.</div></li>
            )}
            {events.slice(0, 10).map((e) => {
              const sev = e.severity.toLowerCase();
              return (
                <li key={e.credit_event_id} className="event">
                  <div className={`event-dot ${sev}`} />
                  <div>
                    <div className="event-title">{e.event_type}</div>
                    <div className="event-meta">
                      <span className="event-source">{e.source}</span>
                      <span>{e.event_date}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 3 }}>{e.description}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card eyebrow="Change orders" title={`CO history (${cos.length})`} info={I.c360Cos}>
          <table className="data-table compact">
            <thead>
              <tr><th>CO</th><th>Project</th><th>Status</th><th>Lag</th><th>Δ Revenue</th></tr>
            </thead>
            <tbody>
              {cos.slice(0, 10).map((co) => (
                <tr key={co.co_id}>
                  <td className="col-strong">{co.co_number}</td>
                  <td className="col-num">{co.proj_id.slice(-8)}</td>
                  <td>{co.status}</td>
                  <td className="col-num">{co.approval_lag_days != null ? `${Math.round(co.approval_lag_days)}d` : "—"}</td>
                  <td className="col-num" style={{ color: co.change_amount_revenue >= 0 ? "var(--good)" : "var(--critical)" }}>
                    {fmtMoneyM(co.change_amount_revenue)}
                  </td>
                </tr>
              ))}
              {cos.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: 12, color: "var(--t4)" }}>No change orders.</td></tr>
              )}
            </tbody>
          </table>
        </Card>

        <Card eyebrow="Recommended" title="Actions for this customer" info={I.c360Actions}>
          <ul className="action-list">
            {(ar?.last_pay_cycle_days ?? 0) > 90 && (
              <li className="action-item">
                <span className="action-arrow">→</span>
                <div className="action-text">
                  Tighten retainage release; require interim lien waivers before next pay app.
                  <span className="action-meta" style={{ display: "block", fontSize: 11, color: "var(--t4)", marginTop: 2 }}>Pay-cycle &gt; 90d</span>
                </div>
              </li>
            )}
            {Math.abs(overUnder) > contract * 0.05 && contract > 0 && (
              <li className="action-item">
                <span className="action-arrow">→</span>
                <div className="action-text">
                  WIP review — over/underbilling exceeds 5% of contract value. Reconcile EAC.
                  <span className="action-meta" style={{ display: "block", fontSize: 11, color: "var(--t4)", marginTop: 2 }}>K-001 trigger</span>
                </div>
              </li>
            )}
            {events.some((e) => e.severity === "Critical" || e.severity === "High") && (
              <li className="action-item">
                <span className="action-arrow">→</span>
                <div className="action-text">
                  Escalate to credit committee — high-severity external distress signal in last 90d.
                  <span className="action-meta" style={{ display: "block", fontSize: 11, color: "var(--t4)", marginTop: 2 }}>K-028 critical</span>
                </div>
              </li>
            )}
            {events.length === 0 && (ar?.last_pay_cycle_days ?? 0) <= 90 && Math.abs(overUnder) <= contract * 0.05 && (
              <li className="action-item">
                <span className="action-arrow">·</span>
                <div className="action-text" style={{ color: "var(--t3)" }}>
                  No actions surfaced. Routine watchlist monitoring.
                </div>
              </li>
            )}
          </ul>
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--t4)" }}>
            Earned-to-date {fmtMoneyM(earned)} · Billed {fmtMoneyM(billed)} · Retainage {fmtMoneyM(retainage)}
          </div>
        </Card>
      </div>
    </section>
  );
}
